import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Send, Sparkles } from 'lucide-react'
import Button from 'components/button/button'
import CareerItem from 'components/career-item/career-item.component'
import CareerCardPlaceholder from 'components/career-item/career-card-placeholder.component'
import BadgeAwarenessCard from 'components/badge-awareness/badge-awareness-card.component'
import { BADGE_SUBTEXT } from 'constants/badge-subtext'
import Loading from 'components/loading/loading.component'
import { useLayout } from 'contexts/LayoutContext'
import { CareerTypeEnum } from 'enums/glimps.enums'
import { getEndUserCareer, getPreviewSideData } from 'services/deeper-dive.service'
import { getCareerSelectionList, CareerSelectionItem } from 'services/on-board'
import { getUserBadgesReport } from 'services/ranking.service'
import { noPreviewImage } from 'utills/image'
import { getCareerStatusText } from 'utills/career-status'
import { uniformCardWidth } from 'utills/card-width'
import { IEndUserCareer } from 'interfaces/CareerInterface'
import { Badge } from 'interfaces/badge.interface'

const HomeScreen: React.FC = () => {
  const navigate = useNavigate()
  const { isChatOpen, setIsChatOpen, setPendingChatMessage, setCareersReorderMode } = useLayout()
  const { userInfo } = useSelector((state: any) => state.auth)
  const firstName: string =
    userInfo?.user?.profile?.first_name ||
    userInfo?.user?.full_name?.split(' ')?.[0] ||
    ''

  const [prompt, setPrompt] = useState('')

  const { data: careers = [], isLoading: careersLoading } = useQuery({
    queryKey: ['homeCareers'],
    queryFn: () => getEndUserCareer(CareerTypeEnum.preview),
    retry: 0,
    refetchOnWindowFocus: false,
  })

  const { data: badges, isLoading: badgesLoading } = useQuery({
    queryKey: ['homeBadges'],
    queryFn: getUserBadgesReport,
    retry: 0,
    refetchOnWindowFocus: false,
  })

  const { data: previewData, isLoading: previewDataLoading } = useQuery({
    queryKey: ['previewSideData'],
    queryFn: getPreviewSideData,
    retry: 0,
    refetchOnWindowFocus: false,
  })

  const { data: selectionList = [], isLoading: selectionListLoading } = useQuery({
    queryKey: ['careerSelectionList'],
    queryFn: getCareerSelectionList,
    retry: 0,
    refetchOnWindowFocus: false,
  })

  // Mirror the careers-page "more to unlock?" check so the home card reflects
  // the same truth: locked+published careers OR a BE-flagged availability of
  // 'continue'/'unlock'. When none of these hold, the user has unlocked
  // everything currently available.
  const lockedPublishedCount = selectionList.filter(
    (c: CareerSelectionItem) => !c.is_unlocked && c.status === 'published',
  ).length
  const availability = previewData?.is_available_careers
  const hasMoreToUnlock =
    availability === 'continue' ||
    availability === 'unlock' ||
    lockedPublishedCount > 0

  const top3 = [...careers]
    .filter((c: IEndUserCareer) => c.category === 'top_three')
    .slice(0, 3)
  const watchList = [...careers]
    .filter((c: IEndUserCareer) => c.category === 'watch_list')
    .slice(0, 7)
  const top3CardWidth = uniformCardWidth(top3.map(c => c.name))
  const watchListCardWidth = uniformCardWidth(watchList.map(c => c.name))
  const continueCareer = careers.find(
    (c: IEndUserCareer) => !c.is_completed,
  )
  const nextUnlocked =
    careers.find(
      (c: IEndUserCareer) => !c.is_deep_dive && !c.is_completed,
    ) || continueCareer

  const goToCareer = (id: string) => {
    const item = careers.find((c: IEndUserCareer) => c.id === id)
    if (!item) return
    if (item.is_completed) {
      navigate(`/user-careers/careers/career-insights/${item.career_id}`)
    } else {
      navigate(`/user-careers/deeper-dive/${item.id}`, {
        state: {
          career_id: item.id,
          cover_video_id: item.cover_video_id,
          deepdive_level: item.deepdive_level,
        },
      })
    }
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const wasChatOpenRef = useRef(false)

  // Restore input only when chat transitions from open → closed
  useEffect(() => {
    if (isChatOpen) {
      wasChatOpenRef.current = true
    } else if (wasChatOpenRef.current) {
      wasChatOpenRef.current = false
      setIsSubmitting(false)
    }
  }, [isChatOpen])

  const submitSherpa = () => {
    const msg = prompt.trim()
    if (!msg) return
    setIsSubmitting(true)
    setPendingChatMessage(msg)
    setPrompt('')
    setIsChatOpen(true)
  }

  if (
    careersLoading ||
    badgesLoading ||
    previewDataLoading ||
    selectionListLoading
  )
    return <Loading />

  // Pick the first not-yet-earned badge (any tier still incomplete).
  // If all earned, fall back to the last one.
  const currentBadge =
    badges?.badges?.find(
      (b: Badge) => b.preview_percentage < 100 || b.deeper_dive_percentage < 100,
    ) || badges?.badges?.[badges?.badges?.length - 1] || badges?.badges?.[0]

  const gentleSpring = { type: 'spring' as const, mass: 1, stiffness: 80, damping: 14 }

  return (
    <motion.div
      className="max-w-[1100px] w-full mx-auto py-16 px-4 flex flex-col gap-10"
      layout
      transition={gentleSpring}
    >
      {/* Welcome + Sherpa search */}
      <motion.div
        className="flex flex-col items-center gap-4 mx-auto w-fit max-w-full px-4"
        animate={{ height: isSubmitting ? 56 : 'auto' }}
        transition={gentleSpring}
      >
        <h1
          className="font-sans font-semibold tracking-normal text-center text-brand whitespace-nowrap"
          style={{ fontSize: 40, lineHeight: '48px' }}
        >
          Welcome back
          {firstName && <span className="capitalize">, {firstName}</span>}
        </h1>
        <AnimatePresence>
          {!isSubmitting && (
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={gentleSpring}
            >
              <div
                className="flex items-center gap-2 bg-white rounded-md px-4 w-fit"
                style={{ minWidth: 437, height: 40, border: '1px solid #E8E8E8' }}
              >
                <Sparkles size={18} className="text-gray-medium shrink-0" />
                <input
                  type="text"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') submitSherpa()
                  }}
                  placeholder="Explore with Career Sherpa or describe what you'd like to do..."
                  aria-label="Ask Career Sherpa"
                  size={51}
                  className="flex-1 bg-transparent text-sm text-gray-dark placeholder-gray-medium outline-none border-none focus:outline-none focus:ring-0 focus:border-none"
                />
              </div>
              <button
                type="button"
                onClick={submitSherpa}
                className="w-10 h-10 rounded-full bg-green flex items-center justify-center text-white shrink-0"
                aria-label="Send to Sherpa"
              >
                <Send size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Top row: Explore more + Your top 3 */}
      <div className="flex gap-6 items-stretch">
        {/* Explore more careers */}
        <div className="relative flex-1 min-w-[411px] h-[265px] rounded-md border border-gray-light bg-[#E8E8E8] pt-2 pr-2 pb-6 pl-2 flex flex-col gap-5 overflow-hidden">
          {hasMoreToUnlock ? (
            <>
              {/* Back navy cards: taller, overflow hidden behind grey */}
              {[
                { top: 8, color: '#0A1640' },
                { top: 16, color: '#14225A' },
              ].map((layer, idx) => (
                <div
                  key={idx}
                  className="absolute rounded-t-md z-10 overflow-hidden"
                  style={{
                    top: layer.top,
                    left: 8,
                    right: 8,
                    height: 81,
                    background: layer.color,
                    boxShadow:
                      '0 2px 3px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
                  }}
                />
              ))}

              {/* Front navy card (no avatar inside) */}
              <div
                className="absolute rounded-t-md z-10"
                style={{
                  top: 24,
                  left: 8,
                  right: 8,
                  height: 81,
                  background: '#213269',
                  boxShadow:
                    '0 2px 3px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
              />

              {/* Avatar on top of navy, extending into grey area (visible through bowl).
                  Centered horizontally so it tracks card width instead of being
                  pinned to the left edge. */}
              <div
                className="absolute w-[120px] h-[120px] rounded-full overflow-hidden bg-white z-20"
                style={{ top: 35, left: '50%', transform: 'translateX(-50%)' }}
              >
                <img
                  src={nextUnlocked?.icon_url || noPreviewImage}
                  alt={nextUnlocked?.name || 'Next'}
                  className="w-full h-full object-cover"
                  style={{ filter: 'blur(3px)' }}
                />
              </div>

              {/* Grey overlay covers card body below navy. Split into responsive
                  pieces centered on the card so the avatar/dip stay aligned at
                  any width:
                    - bottom block: full width below the dip
                    - left strip: top-78 strip up to (center - 40px)
                    - right strip: top-78 strip from (center + 40px) to right
                    - curve piece: 80px-wide div centered with the quadratic dip */}
              <div
                className="absolute z-30 bg-[#E8E8E8]"
                style={{ top: 118, left: 0, right: 0, bottom: 0 }}
              />
              <div
                className="absolute z-30 bg-[#E8E8E8]"
                style={{ top: 78, left: 0, right: 'calc(50% + 40px)', height: 40 }}
              />
              <div
                className="absolute z-30 bg-[#E8E8E8]"
                style={{ top: 78, left: 'calc(50% + 40px)', right: 0, height: 40 }}
              />
              <div
                className="absolute z-30 bg-[#E8E8E8]"
                style={{
                  top: 78,
                  left: '50%',
                  width: 80,
                  height: 40,
                  transform: 'translateX(-50%)',
                  clipPath: 'path("M 0 0 Q 40 40 80 0 L 80 40 L 0 40 Z")',
                }}
              />
              <div className="relative z-40 w-[395px] h-[132px] px-4 flex flex-col gap-4 mt-auto">
                <div className="w-[363px] h-[76px] flex flex-col gap-1.5">
                  <h2 className="font-sans text-2xl font-semibold leading-8 tracking-normal text-brand">
                    Explore more careers
                  </h2>
                  <p className="font-sans text-base font-normal leading-4 tracking-normal text-gray-dark">
                    Watch the preview videos for the next five careers on deck.
                  </p>
                </div>
                <div className="w-[125px] h-[40px]">
                  <Button
                    variant="primary"
                    onClick={() => navigate('/user-careers?tab=preview')}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="relative z-10 h-full w-full px-4 py-4 flex flex-col gap-3 justify-center">
              <div
                className="flex items-center justify-center rounded-full bg-white"
                style={{
                  width: 56,
                  height: 56,
                  border: '2px solid #FFFFFF',
                  boxShadow:
                    '0 1px 2px 0 rgba(0,0,0,0.06), 0 1px 3px 0 rgba(0,0,0,0.1)',
                }}
              >
                <Sparkles size={26} className="text-brand" />
              </div>
              <h2 className="font-sans text-2xl font-semibold leading-8 tracking-normal text-brand">
                You've unlocked them all!
              </h2>
              <p className="font-sans text-base font-normal leading-5 tracking-normal text-gray-dark">
                New careers are on the way — check back soon for fresh paths to explore.
              </p>
            </div>
          )}
        </div>

        {/* Your top 3 */}
        <div className="flex-1 min-w-[411px] h-[265px] rounded-md border border-gray-light bg-[#E8E8E8] p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <h2 className="font-sans text-2xl font-semibold leading-8 tracking-normal text-brand">
              Your top 3
            </h2>
            <button
              type="button"
              onClick={() => {
                // Set deterministically so a stale `true` from elsewhere can't
                // leak across navigations and force reorder mode on a screen
                // with an empty top-3.
                setCareersReorderMode(top3.length > 0)
                navigate('/user-careers')
              }}
              className="font-sans text-base font-semibold leading-5 tracking-normal text-right text-green hover:underline"
            >
              {top3.length === 0 ? 'Find your favorites' : 'Reorder'}
            </button>
          </div>
          <div className="flex justify-around items-start flex-1 gap-4">
            {top3.map((c, i) => (
              <CareerItem
                key={c.id}
                id={c.id}
                name={c.name}
                icon_url={c.icon_url || noPreviewImage}
                rank={i + 1}
                showRanking
                showStatus
                statusText={getCareerStatusText(c)}
                pressed={goToCareer}
                containerHeight={150}
                containerWidth={top3CardWidth}
                height={72}
                width={72}
                fontSize="body-sm-emphasis"
              />
            ))}
            {Array.from({ length: Math.max(0, 3 - top3.length) }).map((_, i) => (
              <CareerCardPlaceholder key={`top3-empty-${i}`} width={top3CardWidth} />
            ))}
          </div>
        </div>
      </div>

      {/* Watch list */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-sans text-2xl font-semibold leading-8 tracking-normal text-brand">
            Your watch list
          </h2>
          <button
            type="button"
            onClick={() => navigate('/user-careers?tab=preview')}
            className="font-sans text-base font-semibold leading-5 tracking-normal text-right text-green hover:underline"
          >
            {watchList.length === 0 ? 'Build your list' : 'View all'}
          </button>
        </div>
        <div className="flex flex-wrap justify-start gap-6 overflow-visible py-3 w-full">
          {watchList.map((c, i) => (
            <CareerItem
              key={c.id}
              id={c.id}
              name={c.name}
              icon_url={c.icon_url || noPreviewImage}
              rank={i + 4}
              showRanking
              showStatus
              statusText={getCareerStatusText(c)}
              pressed={goToCareer}
              containerHeight={150}
              containerWidth={watchListCardWidth}
              height={72}
              width={72}
              fontSize="body-sm-emphasis"
            />
          ))}
          {Array.from({ length: Math.max(0, 7 - watchList.length) }).map((_, i) => (
            <CareerCardPlaceholder key={`watch-empty-${i}`} width={watchListCardWidth} />
          ))}
        </div>
      </div>

      {/* Career awareness */}
      <div className="flex flex-col gap-4 max-w-[846px] w-full h-[228px]">
        <div className="flex items-center justify-between">
          <h2 className="font-sans text-2xl font-semibold leading-8 tracking-normal text-brand">
            Your career awareness
          </h2>
          <button
            type="button"
            onClick={() => navigate('/career-awareness')}
            className="font-sans text-base font-semibold leading-5 tracking-normal text-right text-green hover:underline"
          >
            View details
          </button>
        </div>
        <BadgeAwarenessCard
          width="100%"
          height={180}
          iconSlug={currentBadge?.slug || 'curious'}
          title={currentBadge?.title || 'Curious'}
          subtext={BADGE_SUBTEXT[currentBadge?.slug || 'curious'] || ''}
          progress={[
            {
              label: `${currentBadge?.completed_preview_count ?? 0}/${
                currentBadge?.preview_count ?? 0
              } previews`,
              percent: currentBadge?.preview_percentage ?? 0,
            },
            {
              label: `${currentBadge?.completed_deeper_dive_count ?? 0}/${
                currentBadge?.deeper_dive_count ?? 0
              } deeper dives`,
              percent: currentBadge?.deeper_dive_percentage ?? 0,
            },
          ]}
        />
      </div>
    </motion.div>
  )
}

export default HomeScreen
